import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { notify } from "../../../util/notify";
import axios from "axios";
import { toggleWishlist, isInWishlist } from "../../../util/wishlistService";
import { addToGuestCart, openCartDrawer } from "../../../util/guestCartService";
import { getProductImageUrl, handleProductImageError, extractProductImagePath, applyStoreTitle } from "../../../util/storeLogoHelper";
import { FaHeart, FaFilter, FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import SEOHead from "../../common/SEOHead";
import { PageColors, pageWords, CATEGORY_COLORS, CATEGORY_SCOPE, CATEGORY_TEXTS } from "../../../themes/pageCustomization";
import { ActiveThemeContext } from "../../../themes/themeCache";
import { collectionPageSettings, collectionPageStyle } from "../../../themes/collectionPageSettings";

const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "best", label: "Best selling" },
  { value: "alpha", label: "Alphabetically, A-Z" },
  { value: "alpha_desc", label: "Alphabetically, Z-A" },
  { value: "price_asc", label: "Price, low to high" },
  { value: "price_desc", label: "Price, high to low" },
  { value: "date_desc", label: "Date, new to old" },
  { value: "date_asc", label: "Date, old to new" },
];

const COLOR_SWATCHES = {
  black: "#111827",
  white: "#f8fafc",
  red: "#dc2626",
  blue: "#2563eb",
  green: "#16a34a",
  yellow: "#eab308",
  orange: "#ea580c",
  pink: "#ec4899",
  purple: "#7c3aed",
  brown: "#92400e",
  grey: "#6b7280",
  gray: "#6b7280",
  navy: "#1e3a8a",
  beige: "#d6c4a8",
  gold: "#d4a017",
  silver: "#cbd5e1",
};

function plainText(value) {
  return String(value || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function optionLabel(value) {
  if (typeof value === "string") return value.trim();
  return String(value?.value || "").trim();
}

function productChoices(item) {
  const groups = {};
  (item.options || []).forEach((option) => {
    const name = String(option.name || "").trim();
    if (!name) return;
    const values = (option.values || []).map(optionLabel).filter(Boolean);
    if (values.length) groups[name] = values;
  });
  return groups;
}

function money(value) {
  return `Rs. ${Number(value || 0).toLocaleString()}`;
}

function imagePaths(item) {
  const paths = [];
  const push = (value) => {
    const path = extractProductImagePath(value);
    if (path && !paths.includes(path)) paths.push(path);
  };
  push(item.image);
  (item.productImage || []).forEach((row) => push(row?.image || row));
  return paths;
}

function swatchColor(value) {
  const key = String(value || "").trim().toLowerCase();
  if (key.startsWith("#") && (key.length === 4 || key.length === 7)) return key;
  return COLOR_SWATCHES[key] || "";
}

function prepareProduct(item, categorySlug, categoryName) {
  const pricing = item.pricing || {};
  const finalPrice = Number(pricing.final_price ?? item.productprices?.seeling_price ?? 0);
  const sellingPrice = Number(pricing.selling_price ?? item.productprices?.seeling_price ?? finalPrice);
  return {
    ...item,
    finalPrice,
    sellingPrice,
    hasDiscount: Boolean(pricing.has_discount) && sellingPrice > finalPrice,
    inStock: pricing.in_stock ?? ((item.productprices?.qty ?? item.qty ?? 0) > 0),
    choices: productChoices(item),
    brandName: String(item.brand || "").trim(),
    createdStamp: new Date(item.created_at || 0).getTime() || 0,
    categorySlug: item.category?.slug || categorySlug,
    categoryName: item.category?.name || categoryName,
  };
}

const emptyMeta = { total: 0, page: 1, page_count: 1, catalog_total: 0, facets: { availability: { in: 0, out: 0 }, brands: {}, choices: {}, max_price: 0 } };

export default function ViewProduct(props) {
  const [category, setcategory] = useState({});
  const [pages, setPages] = useState({});
  const [meta, setMeta] = useState(null);
  const [sortBy, setSortBy] = useState("featured");
  const [availability, setAvailability] = useState({ in: true, out: true });
  const [priceFrom, setPriceFrom] = useState("");
  const [priceTo, setPriceTo] = useState("");
  const [priceQuery, setPriceQuery] = useState({ from: "", to: "" });
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedChoices, setSelectedChoices] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [descOpen, setDescOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [, setWishlistVersion] = useState(0);
  const theme = useContext(ActiveThemeContext);
  const look = collectionPageSettings(theme?.theme?.settings || {});
  const words = pageWords(CATEGORY_TEXTS, theme?.theme?.settings || {});
  const pageSize = look.perPage;
  const loadMore = look.pagination === "more";

  const history = useHistory();
  const product_slug = props.match.params.slug;
  const pagesRef = useRef({});
  const inflight = useRef(new Map());
  const slugRef = useRef(product_slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    slugRef.current = product_slug;
    pagesRef.current = {};
    inflight.current = new Map();
    setPages({});
    setMeta(null);
    setcategory({});
    setSortBy("featured");
    setAvailability({ in: true, out: true });
    setPriceFrom("");
    setPriceTo("");
    setPriceQuery({ from: "", to: "" });
    setSelectedBrands([]);
    setSelectedChoices({});
    setDescOpen(false);
    setPage(1);
    setFiltersOpen(false);
    applyStoreTitle(null, "Collection");
  }, [product_slug]);

  // Price typing waits a moment before asking the server.
  useEffect(() => {
    const timer = setTimeout(() => setPriceQuery({ from: priceFrom, to: priceTo }), 400);
    return () => clearTimeout(timer);
  }, [priceFrom, priceTo]);

  const filterKey = useMemo(() => {
    const params = new URLSearchParams();
    params.set("per_page", String(pageSize));
    params.set("sort", sortBy);
    if (!availability.in) params.set("in", "0");
    if (!availability.out) params.set("out", "0");
    if (priceQuery.from !== "") params.set("price_from", priceQuery.from);
    if (priceQuery.to !== "") params.set("price_to", priceQuery.to);
    [...selectedBrands].sort().forEach((brand) => params.append("brands[]", brand));
    const choices = {};
    Object.keys(selectedChoices).sort().forEach((name) => {
      if (selectedChoices[name].length) choices[name] = [...selectedChoices[name]].sort();
    });
    if (Object.keys(choices).length) params.set("choices", JSON.stringify(choices));
    return params.toString();
  }, [pageSize, sortBy, availability, priceQuery, selectedBrands, selectedChoices]);

  // Loads one page from the server. Pages are cached per filter, and a page
  // already on its way is not requested twice.
  const loadPage = useCallback((key, number) => {
    const cacheKey = `${key}&page=${number}`;
    if (pagesRef.current[cacheKey]) return Promise.resolve(pagesRef.current[cacheKey]);
    if (inflight.current.has(cacheKey)) return inflight.current.get(cacheKey);
    const slug = product_slug;
    const request = axios.get(`/api/fetch-product/${slug}?${cacheKey}`).then((res) => {
      if (slug !== slugRef.current) return null;
      if (res.data.status === 200) {
        const entry = { items: res.data.product_data?.product || [], meta: { ...emptyMeta, ...(res.data.meta || {}) } };
        pagesRef.current[cacheKey] = entry;
        setPages((current) => ({ ...current, [cacheKey]: entry }));
        const nextCategory = res.data.product_data?.category || {};
        setcategory((current) => (current.id === nextCategory.id ? current : nextCategory));
        return entry;
      }
      if (res.data.status === 400 || res.data.status === 404) {
        notify(res.data.message || "This collection is not available.", "error");
        history.push("/collection");
      }
      return null;
    }).finally(() => {
      inflight.current.delete(cacheKey);
    });
    inflight.current.set(cacheKey, request);
    return request;
  }, [product_slug, history]);

  useEffect(() => {
    let active = true;
    loadPage(filterKey, page).then((entry) => {
      if (!active || !entry) return;
      setMeta(entry.meta);
      // Prefetch the next page so moving forward is instant.
      if (page < entry.meta.page_count) loadPage(filterKey, page + 1).catch(() => {});
    }).catch(() => {
      if (active) setMeta((current) => current || emptyMeta);
    });
    return () => {
      active = false;
    };
  }, [filterKey, page, loadPage]);

  useEffect(() => {
    if (category.name) applyStoreTitle(null, category.name);
  }, [category.name]);

  useEffect(() => {
    if (!filtersOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event) => {
      if (event.key === "Escape") setFiltersOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [filtersOpen]);

  const loading = meta === null;
  const info = meta || emptyMeta;
  const facets = info.facets || emptyMeta.facets;
  const currentEntry = pages[`${filterKey}&page=${page}`];
  const waiting = !currentEntry;

  const pageItems = useMemo(() => {
    if (!loadMore) return currentEntry ? currentEntry.items : [];
    const list = [];
    for (let number = 1; number <= page; number += 1) {
      const entry = pages[`${filterKey}&page=${number}`];
      if (!entry) break;
      list.push(...entry.items);
    }
    return list;
  }, [loadMore, currentEntry, pages, filterKey, page]);

  const visibleProducts = useMemo(
    () => pageItems.map((item) => prepareProduct(item, product_slug, category.name)),
    [pageItems, product_slug, category.name]
  );

  const catalogMax = facets.max_price || 0;
  const brands = Object.keys(facets.brands || {});
  const choiceGroups = Object.entries(facets.choices || {}).map(([name, values]) => ({ name, values: Object.keys(values) }));
  const totalMatches = info.total || 0;
  const pageCount = Math.max(1, info.page_count || 1);
  const safePage = Math.min(page, pageCount);

  const description = plainText(category.description || category.meta_descrip);

  const filtersActive = (
    !availability.in || !availability.out
    || priceFrom !== ""
    || priceTo !== ""
    || selectedBrands.length > 0
    || Object.values(selectedChoices).some((values) => values.length > 0)
  );

  const clearFilters = () => {
    setAvailability({ in: true, out: true });
    setPriceFrom("");
    setPriceTo("");
    setSelectedBrands([]);
    setSelectedChoices({});
    setPage(1);
  };

  const toggleList = (list, value, setter) => {
    setter(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
    setPage(1);
  };

  const toggleChoice = (name, value) => {
    setSelectedChoices((current) => {
      const list = current[name] || [];
      const next = list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
      return { ...current, [name]: next };
    });
    setPage(1);
  };

  const handleWishlistToggle = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    toggleWishlist({
      id: item.id,
      name: item.name,
      slug: item.slug,
      categorySlug: item.categorySlug,
      categoryName: item.categoryName,
      image: extractProductImagePath(item) || item?.image,
      price: item.finalPrice,
    });
    setWishlistVersion((value) => value + 1);
  };

  const goToPage = (next) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const chips = [];
  if (!availability.in && availability.out) chips.push({ key: "out", label: "Out of stock", clear: () => setAvailability({ in: true, out: true }) });
  if (availability.in && !availability.out) chips.push({ key: "in", label: "In stock", clear: () => setAvailability({ in: true, out: true }) });
  if (!availability.in && !availability.out) chips.push({ key: "none", label: "Availability hidden", clear: () => setAvailability({ in: true, out: true }) });
  if (priceFrom !== "" || priceTo !== "") {
    const fromLabel = priceFrom === "" ? "0" : Number(priceFrom).toLocaleString();
    const toLabel = priceTo === "" ? catalogMax.toLocaleString() : Number(priceTo).toLocaleString();
    chips.push({
      key: "price",
      label: `Rs. ${fromLabel} – Rs. ${toLabel}`,
      clear: () => {
        setPriceFrom("");
        setPriceTo("");
      },
    });
  }
  selectedBrands.forEach((brand) => {
    chips.push({ key: `brand-${brand}`, label: brand, clear: () => toggleList(selectedBrands, brand, setSelectedBrands) });
  });
  Object.entries(selectedChoices).forEach(([name, values]) => {
    values.forEach((value) => {
      chips.push({ key: `${name}-${value}`, label: `${name}: ${value}`, clear: () => toggleChoice(name, value) });
    });
  });

  const filterGroups = (
    <>
              <details open>
                <summary>{words.availability}</summary>
                <label>
                  <input
                    type="checkbox"
                    checked={availability.in}
                    onChange={(event) => {
                      setAvailability((current) => ({ ...current, in: event.target.checked }));
                      setPage(1);
                    }}
                  />
                  <span>{words.inStock}</span>
                  <small>{facets.availability?.in || 0}</small>
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={availability.out}
                    onChange={(event) => {
                      setAvailability((current) => ({ ...current, out: event.target.checked }));
                      setPage(1);
                    }}
                  />
                  <span>{words.outOfStock}</span>
                  <small>{facets.availability?.out || 0}</small>
                </label>
              </details>

              <details open>
                <summary>{words.price}</summary>
                <div className="shop-price-fields">
                  <label>
                    From
                    <input
                      type="number"
                      min="0"
                      inputMode="numeric"
                      value={priceFrom}
                      placeholder="0"
                      onChange={(event) => {
                        setPriceFrom(event.target.value);
                        setPage(1);
                      }}
                    />
                  </label>
                  <label>
                    To
                    <input
                      type="number"
                      min="0"
                      inputMode="numeric"
                      value={priceTo}
                      placeholder={catalogMax ? String(catalogMax) : "0"}
                      onChange={(event) => {
                        setPriceTo(event.target.value);
                        setPage(1);
                      }}
                    />
                  </label>
                </div>
                {catalogMax > 0 && <p className="shop-price-note">The highest price is {money(catalogMax)}</p>}
              </details>

              {brands.length > 0 && (
                <details open>
                  <summary>{words.brand}</summary>
                  {brands.map((brand) => (
                    <label key={brand}>
                      <input
                        type="checkbox"
                        checked={selectedBrands.includes(brand)}
                        onChange={() => toggleList(selectedBrands, brand, setSelectedBrands)}
                      />
                      <span>{brand}</span>
                      <small>{facets.brands[brand] || 0}</small>
                    </label>
                  ))}
                </details>
              )}

              {choiceGroups.map((group) => (
                <details key={group.name} open>
                  <summary>{group.name}</summary>
                  {group.values.map((value) => (
                    <label key={value}>
                      <input
                        type="checkbox"
                        checked={(selectedChoices[group.name] || []).includes(value)}
                        onChange={() => toggleChoice(group.name, value)}
                      />
                      <span>{value}</span>
                      <small>{facets.choices[group.name]?.[value] || 0}</small>
                    </label>
                  ))}
                </details>
              ))}
    </>
  );

  return (
    <div
      data-theme-group="collection-page"
      data-theme-label="Category page"
      className={`shop-collection${look.titleAlign === "center" ? " is-center" : ""}`}
      style={collectionPageStyle(look)}
    >
      <PageColors scope={CATEGORY_SCOPE} colors={CATEGORY_COLORS} settings={theme?.theme?.settings || {}} />
      {category && (
        <SEOHead
          title={category.meta_title || (category.name ? `${category.name}` : "Collection")}
          description={category.meta_descrip || category.description}
          keywords={category.meta_keyword}
          image={category.image}
          url={`/collection/${product_slug}`}
          type="website"
          category={category}
        />
      )}

      <div className="container shop-collection-wrap">
        {look.showBreadcrumbs && (
          <ul className="shop-crumbs">
            <li><Link to="/">Home</Link></li>
            <li aria-hidden="true">/</li>
            <li><Link to="/collection">Collections</Link></li>
            <li aria-hidden="true">/</li>
            <li className="is-current">{category.name || "Products"}</li>
          </ul>
        )}

        <header className="shop-collection-head">
          <h1>{category.name || "Collection"}</h1>
          {look.showDescription && description && (
            <div className="shop-collection-desc">
              <p className={descOpen ? "" : "is-clamped"}>{description}</p>
              {description.length > 180 && (
                <button type="button" onClick={() => setDescOpen((open) => !open)}>
                  {descOpen ? "Show less" : "Show more"}
                </button>
              )}
            </div>
          )}
        </header>

        {loading ? (
          <div className="shop-collection-grid">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <div className="shop-card is-loading" key={n}>
                <div className="shop-card-media" />
                <div className="shop-card-line" />
                <div className="shop-card-line is-short" />
              </div>
            ))}
          </div>
        ) : info.catalog_total === 0 ? (
          <div className="shop-empty">
            <h2>{words.noProducts}</h2>
            <p>We are currently updating the inventory for {category.name || "this collection"}.</p>
            <Link to="/collection" className="shop-clear">Browse collections</Link>
          </div>
        ) : (
          <div className={`shop-browse${look.showFilter && look.filterStyle === "sidebar" ? " is-sidebar" : ""}`}>
            {look.showFilter && look.filterStyle === "sidebar" && (
              <aside className="shop-sidebar">{filterGroups}</aside>
            )}
            <div>
            <div className="shop-toolbar">
              {look.showFilter && (
                <button type="button" className="shop-filter-btn" onClick={() => setFiltersOpen(true)}>
                  <FaFilter size={12} />
                  {words.filter}
                  {filtersActive ? <span className="shop-filter-count">{chips.length}</span> : null}
                </button>
              )}
              <div className="shop-toolbar-end">
                {look.showCount && (
                  <span className="shop-count">{totalMatches} {totalMatches === 1 ? words.product : words.products}</span>
                )}
                {look.showSort && (
                <label className="shop-sort">
                  <span>{words.sortBy}</span>
                  <select
                    aria-label="Sort by"
                    value={sortBy}
                    onChange={(event) => {
                      setSortBy(event.target.value);
                      setPage(1);
                    }}
                  >
                    {SORTS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                )}
              </div>
            </div>

            {chips.length > 0 && (
              <div className="shop-chips">
                {chips.map((chip) => (
                  <button key={chip.key} type="button" className="shop-chip" onClick={() => { chip.clear(); setPage(1); }}>
                    {chip.label}
                    <FaTimes size={9} />
                  </button>
                ))}
                <button type="button" className="shop-clear" onClick={clearFilters}>{words.clearAll}</button>
              </div>
            )}

            {visibleProducts.length === 0 && waiting ? (
              <div className="shop-collection-grid" aria-busy="true">
                {Array.from({ length: Math.min(pageSize, 8) }, (_, n) => (
                  <div className="shop-card is-loading" key={n}>
                    <div className="shop-card-media" />
                    <div className="shop-card-line" />
                    <div className="shop-card-line is-short" />
                  </div>
                ))}
              </div>
            ) : visibleProducts.length === 0 ? (
              <div className="shop-empty">
                <h2>{words.emptyTitle}</h2>
                <p>{words.emptyText}</p>
                <button type="button" className="shop-clear" onClick={clearFilters}>{words.clearAll}</button>
              </div>
            ) : (
              <div className="shop-collection-grid" style={waiting && !loadMore ? { opacity: 0.55, transition: "opacity .15s" } : undefined} aria-busy={waiting}>
                {visibleProducts.map((item) => {
                  const photos = imagePaths(item);
                  const productInWishlist = isInWishlist(item.id);
                  const href = `/collection/${item.categorySlug}/${item.slug}`;
                  const hasChoices = Object.keys(item.choices).length > 0;
                  const colorValues = item.choices.Color || item.choices.Colour || item.choices.color || [];
                  return (
                    <article className="shop-card" key={item.id}>
                      <div className="shop-card-media">
                        <Link to={href}>
                          <img src={getProductImageUrl(photos[0] || item)} alt={item.name} onError={handleProductImageError} loading="lazy" decoding="async" />
                          {look.showHoverImage && photos[1] && (
                            <img className="is-hover" src={getProductImageUrl(photos[1])} alt="" onError={handleProductImageError} loading="lazy" decoding="async" />
                          )}
                        </Link>
                        <div className="shop-card-badges">
                          {look.showSoldOut && !item.inStock && (
                            <span style={{ background: look.soldBackground, color: look.soldText }}>{look.soldLabel}</span>
                          )}
                          {look.showSaleBadge && item.inStock && item.hasDiscount && (
                            <span style={{ background: look.saleBackground, color: look.saleText }}>{look.saleLabel}</span>
                          )}
                        </div>
                        {look.showWishlist && (
                        <button
                          type="button"
                          className={`shop-card-wish ${productInWishlist ? "is-on" : ""}`}
                          onClick={(event) => handleWishlistToggle(event, item)}
                          aria-label={productInWishlist ? "Remove from wishlist" : "Add to wishlist"}
                        >
                          <FaHeart size={12} />
                        </button>
                        )}
                        {look.showQuickAdd && (
                        <div className="shop-card-quick">
                          {hasChoices ? (
                            <Link to={href}>{look.chooseLabel}</Link>
                          ) : (
                            <button
                              type="button"
                              disabled={!item.inStock}
                              onClick={() => {
                                addToGuestCart(item, 1);
                                openCartDrawer();
                              }}
                            >
                              {item.inStock ? look.addLabel : look.soldLabel}
                            </button>
                          )}
                        </div>
                        )}
                      </div>
                      <div className="shop-card-info">
                        {look.showVendor && item.brandName && <p className="shop-card-vendor">{item.brandName}</p>}
                        <Link to={href}><h2>{item.name}</h2></Link>
                        <p className="shop-card-price">
                          <span>{money(item.finalPrice)}</span>
                          {item.hasDiscount && <s>{money(item.sellingPrice)}</s>}
                        </p>
                        {look.showSwatches && colorValues.length > 0 && (
                          <div className="shop-swatches">
                            {colorValues.slice(0, 5).map((value) => {
                              const color = swatchColor(value);
                              return color ? (
                                <span key={value} title={value} style={{ background: color }} />
                              ) : (
                                <em key={value}>{value}</em>
                              );
                            })}
                            {colorValues.length > 5 && <em>+{colorValues.length - 5}</em>}
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}

            {loadMore && visibleProducts.length > 0 && page < pageCount && (
              <button type="button" className="shop-load" disabled={waiting} onClick={() => setPage((number) => number + 1)}>
                {waiting ? "Loading…" : words.loadMore}
              </button>
            )}
            {look.pagination !== "more" && pageCount > 1 && (
              <nav className="shop-pages" aria-label="Collection pages">
                <button type="button" disabled={safePage === 1} onClick={() => goToPage(safePage - 1)} aria-label="Previous page">
                  <FaChevronLeft size={11} />
                </button>
                {Array.from({ length: pageCount }, (_, index) => index + 1)
                  .filter((number) => number === 1 || number === pageCount || Math.abs(number - safePage) <= 1)
                  .map((number, index, list) => (
                    <React.Fragment key={number}>
                      {index > 0 && list[index - 1] !== number - 1 && <span>…</span>}
                      <button
                        type="button"
                        className={number === safePage ? "is-current" : ""}
                        onClick={() => goToPage(number)}
                        aria-current={number === safePage ? "page" : undefined}
                      >
                        {number}
                      </button>
                    </React.Fragment>
                  ))}
                <button type="button" disabled={safePage === pageCount} onClick={() => goToPage(safePage + 1)} aria-label="Next page">
                  <FaChevronRight size={11} />
                </button>
              </nav>
            )}
            </div>
          </div>
        )}
      </div>

      {look.showFilter && filtersOpen && (
        <div className="shop-drawer" role="presentation" onClick={() => setFiltersOpen(false)}>
          <aside
            className="shop-drawer-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Filter"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="shop-drawer-head">
              <h2>{words.filter}</h2>
              <button type="button" onClick={() => setFiltersOpen(false)} aria-label="Close filters">
                <FaTimes size={14} />
              </button>
            </div>
            <div className="shop-drawer-body">{filterGroups}</div>
            <div className="shop-drawer-foot">
              <button type="button" onClick={() => setFiltersOpen(false)}>
                View {totalMatches} {totalMatches === 1 ? words.product : words.products}
              </button>
              {filtersActive && (
                <button type="button" className="shop-clear" onClick={clearFilters}>{words.clearAll}</button>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
